import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useNFC } from "@/hooks/useNFC";

export function LoginForm() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { setAuth } = useAuth();
	const [error, setError] = useState<string | null>(null);
	const nfc = useNFC();

	const loginMutation = useMutation({
		mutationFn: (data: LoginRequest) => {
			if (data.type === "nfc") {
				return loginNFC(data.email, data.hash);
			}

			return login(data.email, data.password);
		},
		onSuccess: async (res) => {
			if (res.status === "error") {
				setError(res.message ?? "Error desconocido al autenticar");
				return;
			}

			setAuth(res.token);
			void queryClient.invalidateQueries();
			await navigate(res.goTo);
		},
		onError: (err) => {
			setError("Error en el login");
			console.error(err);
		},
	});

	const isAuthenticating = loginMutation.isPending;

	const onSubmit = useCallback((event: React.SubmitEvent) => {
		event.preventDefault();

		if (isAuthenticating) return;

		const email = (event.target.elements.namedItem("email") as HTMLInputElement).value;
		const password = (event.target.elements.namedItem("password") as HTMLInputElement).value;

		setError(null);

		loginMutation.mutate({
			type: "password",
			email,
			password,
		});
	}, [isAuthenticating, loginMutation]);

	useEffect(() => {
		if (nfc.data == null || isAuthenticating) {
			return;
		}

		const data = nfc.data as { email: string; hash: string; };

		if (
			typeof data.email !== "string" ||
			typeof data.hash !== "string" ||
			!data.email.trim() ||
			!data.hash.trim()
		) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setError("Los datos de la tarjeta NFC no son válidos");
			return;
		}

		setError(null);

		loginMutation.mutate({
			type: "nfc",
			email: data.email,
			hash: data.hash,
		});
	}, [nfc.data, loginMutation.mutate, isAuthenticating, loginMutation]);

	return (
		<form role="form" className="login-form" onSubmit={(event) => { onSubmit(event); }} aria-busy={isAuthenticating}>
			{error != null && (
				<div id="contenedorErrores" style={{ fontWeight: "bold", color: "red", textAlign: "center", padding: "1em" }}>
					<div className="error">{error}</div>
				</div>
			)}

			<div className="form-group">
				<input type="email" className="form-control" name="email" placeholder="Email" autoComplete="username" disabled={isAuthenticating} />
			</div>
			<div className="form-group">
				<input type="password" className="form-control" name="password" placeholder="Password" autoComplete="current-password" disabled={isAuthenticating} />
			</div>

			<button type="submit" className="btnPrimary" style={{ width: "100%", marginBottom: 10 }} disabled={isAuthenticating}>
				{isAuthenticating ? "Iniciando sesión..." : "Login"}
			</button>
			{nfc.nfcSupported == true && (
				nfc.isReading == true ? (
					<button type="button" className="btnPrimary" style={{ width: "100%", marginBottom: 10 }} onClick={() => { nfc.stopNFCReading(); }} disabled={isAuthenticating}>Cancelar</button>
				) : (
					<button type="button" className="btnPrimary" style={{ width: "100%", marginBottom: 10 }} onClick={() => void nfc.startNFCReading()} disabled={isAuthenticating}>Leer NFC</button>
				)
			)}

			<Link to="/recuperarcontrasena">¿Olvidaste la contraseña? Haz click aquí</Link>
		</form>
	);
}

function login(email: string, password: string) {
	return sendAjaxRequest<LoginResponse>("POST", "/api/v1/public/login", { username: email, password: password, plataforma: "web" });
}

function loginNFC(email: string, nfctag: string) {
	return sendAjaxRequest<LoginResponse>("POST", "/api/v1/public/loginFromNFCTag", { email, nfctag, plataforma: "web" });
}


type LoginRequest = {
	type: "password";
	email: string;
	password: string;
} | {
	type: "nfc";
	email: string;
	hash: string;
};

type LoginResponse = {
	status: "error";
	code?: string;
	message?: string;
} | {
	status: "success";
	goTo: string;
	token: string;
	endpoints: unknown;
	datosUsuario: {
		email: string;
		rol: string;
	};
};